import React, { useRef, useState } from "react";
import { observer } from "mobx-react";

function formatMMSS(ms) {
  const sec = Math.floor(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const SOFT_WARN_MS = 10 * 60 * 1000;

export const FloatingRecordingPill = observer(function FloatingRecordingPill({ controller }) {
  const warn = controller.elapsedMs >= SOFT_WARN_MS;
  // null = use the CSS default (centered along the bottom). Once dragged, an
  // explicit {x, y} pins the pill where the user dropped it.
  const [pos, setPos] = useState(null);
  const pillRef = useRef(null);
  // null = not dragging; {dx, dy, w, h} captures the grab offset + size while a
  // drag is in flight. A ref (not state) so the move/up handlers never read a
  // stale value.
  const grab = useRef(null);

  const onPointerDown = (e) => {
    // Let the Stop button keep its click — never start a drag from it.
    if (e.target.closest(".cua-pill-stop")) return;
    const rect = pillRef.current.getBoundingClientRect();
    grab.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, w: rect.width, h: rect.height };
    // Capture the pointer so we keep getting move/up events even when the
    // cursor passes over the Guacamole iframe — without this, the iframe
    // swallows the events and the pill stops following the cursor.
    pillRef.current.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!grab.current) return;
    const maxX = window.innerWidth - grab.current.w;
    const maxY = window.innerHeight - grab.current.h;
    setPos({
      x: Math.max(0, Math.min(maxX, e.clientX - grab.current.dx)),
      y: Math.max(0, Math.min(maxY, e.clientY - grab.current.dy)),
    });
  };

  const onPointerUp = (e) => {
    if (!grab.current) return;
    grab.current = null;
    pillRef.current.releasePointerCapture?.(e.pointerId);
  };

  const style = pos
    ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto", transform: "none" }
    : undefined;

  return (
    <div
      ref={pillRef}
      className={`cua-pill ${warn ? "cua-pill-warning" : ""}`}
      role="status"
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      <span className="cua-pill-dot" aria-hidden="true" />
      <span className="cua-pill-time">
        {warn ? "⚠ " : ""}
        {formatMMSS(controller.elapsedMs)}
      </span>
      {controller.micLevel === null ? (
        <span className="cua-pill-no-mic">🔇 No narration</span>
      ) : (
        <span className="cua-pill-bars" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <span
              key={i}
              className="cua-pill-bar"
              style={{
                height: `${Math.max(
                  10,
                  Math.min(100, controller.micLevel * 100 * (0.7 + i * 0.05)),
                )}%`,
              }}
            />
          ))}
        </span>
      )}
      <button
        type="button"
        className="cua-pill-stop"
        onClick={() => controller.stopRecording()}
      >
        ■ Stop
      </button>
    </div>
  );
});
